<template>
  <div class="p-6 space-y-8 text-slate-900 dark:text-white">
    <!-- Welcome Header -->
    <div
      class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
    >
      <div>
        <h1 class="text-3xl font-black tracking-tight">
          {{ $t("welcome_back") }},
          <span class="text-primary">{{ username }}</span
          >!
        </h1>
        <p
          class="text-sm text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1"
        >
          {{
            isAdmin || isModerator ? $t("system_overview") : $t("my_overview")
          }}
        </p>
      </div>

      <!-- <BaseButton @click="sendTestNotification">
        🧪 Test Notification
        </BaseButton> -->

      <div
        class="flex items-center gap-3 bg-white dark:bg-dark-surface p-2 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm transition-colors"
      >
        <div
          class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary text-xl"
        >
          <i class="fas fa-calendar-day"></i>
        </div>
        <div class="pr-4">
          <p
            class="text-[10px] font-black text-slate-400 uppercase tracking-tighter"
          >
            {{ $t("todays_date") }}
          </p>
          <p class="text-xs font-black">{{ todayDate }}</p>
        </div>
      </div>
    </div>

    <!-- Stats Grid Component -->
    <DashboardStats :stats="currentStats" :loading="isLoadingStats" />

    <!-- Main Content Row -->
    <div class="grid lg:grid-cols-3 gap-8">
      <!-- Recent Activity Component -->
      <div class="lg:col-span-2 space-y-6">
        <DashboardRecentActivity
          :bookings="recentBookings"
          :loading="isLoadingBookings"
          :error="bookingsError"
          :is-admin="isAdmin || isModerator"
        />

        <!-- Recent Messages Component (Admin Only) -->
        <DashboardRecentMessages
          v-if="isAdmin || isModerator"
          :chats="recentChats"
          :loading="isLoadingChats"
          :error="chatsError"
        />
      </div>

      <!-- Quick Actions / Sidebar -->
      <div class="space-y-6">
        <!-- Promo Banner -->
        <DashboardPromoBanner />

        <!-- Quick Settings Component -->
        <DashboardQuickSettings
          :is-dark="isDark"
          :locale="locale"
          @toggle-theme="toggleTheme"
          @change-locale="changeLocale"
        />
      </div>
    </div>

    <!-- Analytics Section (Admin / Moderator Only) -->
    <DashboardAnalytics
      v-if="isAdmin || isModerator"
      :analytics="analyticsData"
      :loading="isLoadingAnalytics"
      :error="analyticsError"
      :is-admin="isAdmin"
      :is-moderator="isModerator"
      :booking-stats="adminStats"
    />
  </div>
</template>

<script>
import authService from "@/services/auth.service";
import bookingService from "@/services/booking.service";
import chatService from "@/services/chat.service";
import { isDark, toggleTheme } from "@/utils/theme";
import { useI18n } from "vue-i18n";
import adminNotificationService from "@/services/adminNotification.service";

import DashboardStats from "@/components/dashboard/DashboardStats.vue";
import DashboardRecentActivity from "@/components/dashboard/DashboardRecentActivity.vue";
import DashboardRecentMessages from "@/components/dashboard/DashboardRecentMessages.vue";
import DashboardQuickSettings from "@/components/dashboard/DashboardQuickSettings.vue";
import DashboardPromoBanner from "@/components/dashboard/DashboardPromoBanner.vue";
import DashboardAnalytics from "@/components/dashboard/DashboardAnalytics.vue";
import BaseButton from "@/components/Button.vue";

export default {
  name: "HomeView",
  components: {
    DashboardStats,
    DashboardRecentActivity,
    DashboardRecentMessages,
    DashboardQuickSettings,
    DashboardPromoBanner,
    DashboardAnalytics,
    BaseButton,
  },
  setup() {
    const { locale, t } = useI18n();
    const changeLocale = (lang) => {
      locale.value = lang;
    };
    return { isDark, toggleTheme, locale, changeLocale, t };
  },
  data() {
    return {
      adminStats: {},
      hostStats: {},
      recentChats: [],
      recentBookings: [],
      username: authService.getUser()?.username || "Admin",

      // Loading states
      isLoadingStats: false,
      isLoadingChats: false,
      isLoadingBookings: false,

      // Error states
      statsError: null,
      chatsError: null,
      bookingsError: null,

      analyticsData: null,
      isLoadingAnalytics: false,
      analyticsError: null,
    };
  },
  computed: {
    roles() {
      return authService.getUser()?.roles || [];
    },
    isAdmin() {
      return this.roles.includes("admin");
    },
    isModerator() {
      return this.roles.includes("moderator");
    },
    isHost() {
      return this.roles.includes("host");
    },
    todayDate() {
      return new Date().toLocaleDateString(
        this.locale === "ar" ? "ar-EG" : "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
    },
    currentStats() {
      if (this.isAdmin || this.isModerator) {
        return [
          {
            label: "total_bookings",
            value: this.adminStats.total || 0,
            type: "number",
            icon: "fas fa-calendar-alt",
            color: "text-blue-500",
            link: "/admin/bookings",
          },
          {
            label: "pending_bookings",
            value: this.adminStats.pending || 0,
            type: "number",
            icon: "fas fa-clock",
            color: "text-amber-500",
            link: "/admin/bookings?status=pending",
          },
          {
            label: "active_cars",
            value: this.adminStats.total_cars || 0,
            type: "number",
            icon: "fas fa-car-side",
            color: "text-green-500",
            link: "/cars",
          },
          {
            label: "total_revenue",
            value: this.adminStats.revenue || 0,
            type: "currency",
            icon: "fas fa-dollar-sign",
            color: "text-primary",
            link: null,
          },
        ];
      }
      return [
        {
          label: "active_cars",
          value: this.hostStats.total_cars || 0,
          type: "number",
          icon: "fas fa-car-side",
          color: "text-blue-500",
          link: "/cars",
        },
        {
          label: "pending_bookings",
          value: this.hostStats.pending || 0,
          type: "number",
          icon: "fas fa-clock",
          color: "text-amber-500",
          link: "/host/bookings",
        },
        {
          label: "completed_bookings",
          value: this.hostStats.completed || 0,
          type: "number",
          icon: "fas fa-check-circle",
          color: "text-green-500",
          link: "/host/bookings",
        },
        {
          label: "total_revenue",
          value: this.hostStats.revenue || 0,
          type: "currency",
          icon: "fas fa-dollar-sign",
          color: "text-primary",
          link: null,
        },
      ];
    },
  },
  mounted() {
    this.isLoadingStats = true;
    this.isLoadingBookings = true;
    if (this.isAdmin || this.isModerator) {
      this.isLoadingChats = true;
    }

    const promises = [this.loadStats(), this.loadRecentBookings()];

    if (this.isAdmin || this.isModerator) {
      promises.push(this.loadRecentChats());
    }

    Promise.all(promises).catch((err) => {
      console.error("Error fetching dashboard data:", err);
    });

    if (this.isAdmin || this.isModerator) {
      promises.push(this.loadAnalytics());
    }
  },
  methods: {
    async loadStats() {
      this.isLoadingStats = true;
      this.statsError = null;
      try {
        const res = await bookingService.getStats();
        const data = res.data?.data || res.data || {};
        if (this.isAdmin || this.isModerator) {
          this.adminStats = data;
        } else {
          this.hostStats = data;
        }
      } catch (err) {
        this.statsError =
          err.response?.data?.message || "Failed to load statistics.";
      } finally {
        this.isLoadingStats = false;
      }
    },
    async loadRecentBookings() {
      this.isLoadingBookings = true;
      this.bookingsError = null;
      try {
        let res;
        if (this.isAdmin || this.isModerator) {
          res = await bookingService.getAllBookings({ per_page: 5 });
        } else {
          res = await bookingService.getOwnerBookings({ per_page: 5 });
        }
        this.recentBookings = res.data?.data || [];
      } catch (err) {
        this.bookingsError =
          err.response?.data?.message || "Failed to load recent activity.";
      } finally {
        this.isLoadingBookings = false;
      }
    },
    async loadRecentChats() {
      this.isLoadingChats = true;
      this.chatsError = null;
      try {
        const res = await chatService.getAllChats({ per_page: 5 });
        this.recentChats = res.data?.data || [];
      } catch (err) {
        this.chatsError =
          err.response?.data?.message || "Failed to load recent messages.";
      } finally {
        this.isLoadingChats = false;
      }
    },

    async loadAnalytics() {
      this.isLoadingAnalytics = true;
      this.analyticsError = null;
      try {
        const res = await bookingService.getAnalytics();
        this.analyticsData = res.data?.data || null;
      } catch (err) {
        this.analyticsError =
          err.response?.data?.message || "Failed to load analytics.";
      } finally {
        this.isLoadingAnalytics = false;
      }
    },

    async sendTestNotification() {
      try {
        await adminNotificationService.sendTestNotification();

        console.log("Test notification sent");
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped></style>
